import React from 'react';
import { Link } from 'react-router-dom';
import FundWiseLogo from './FundWiseLogo';
import { ArrowLeft } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <FundWiseLogo size="md" variant="default" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Terms and Conditions</h1>
          <p className="text-slate-600 mb-8">Effective Date: October 4, 2025 | Last Updated: October 4, 2025</p>

          <div className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">1. Service Provider Details</h2>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong className="text-slate-900">Service Name:</strong> Portfolio Auditor</li>
                <li><strong className="text-slate-900">Business Type:</strong> Software as a Service (SaaS)</li>
                <li><strong className="text-slate-900">Business Location:</strong> Bangalore, India</li>
                <li><strong className="text-slate-900">Website:</strong> {process.env.REACT_APP_COMPANY_WEBSITE || 'www.portfolioauditor.com'}</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">2. Acceptance of Terms</h2>
              
              <p className="text-slate-700 leading-relaxed">
                By accessing and using Portfolio Auditor's portfolio analysis software and services ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use our Service.
              </p>
              
              <p className="text-slate-700 leading-relaxed">
                These Terms constitute a legally binding agreement between you ("User," "you," or "your") and Portfolio Auditor ("we," "us," or "our").
              </p>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">3. Service Description</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">3.1 Portfolio Auditor Software</h3>
                <p className="text-slate-700 leading-relaxed"><strong className="text-slate-900">Platform:</strong> Desktop software for Windows PC using Microsoft Power BI</p>
                <p className="text-slate-700 leading-relaxed"><strong className="text-slate-900">Primary Service:</strong> Portfolio Auditor provides portfolio analysis software that enables users to:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Analyze mutual fund portfolio performance locally on their PC</li>
                  <li>Compare portfolio performance against benchmark indices and custom funds</li>
                  <li>Process CAMS PDF statements with complete privacy</li>
                  <li>Generate comprehensive analysis reports with true apple-to-apple comparisons</li>
                  <li>Track daily portfolio changes and performance metrics</li>
                </ul>
              </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-6">
              <h4 className="text-blue-800 font-semibold mb-2">🔒 Privacy-First Design</h4>
              <p className="text-blue-700 text-sm mb-0">
                <strong>Key Feature:</strong> All analysis happens on YOUR PC. Your financial data never leaves your computer, ensuring complete privacy and control.
              </p>
            </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">3.2 System Requirements</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Windows PC/Laptop (Mac support in development)</li>
                  <li>Microsoft Power BI Desktop (free download)</li>
                  <li>Internet connection for software download and license activation</li>
                  <li>CAMS statement access for portfolio analysis</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">4. Subscription Plans & Pricing</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">4.1 Beta Launch Special</h3>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4">
                  <p className="text-orange-800 font-semibold mb-0">🚀 Beta Launch: {process.env.REACT_APP_BETA_PERIOD || '1 month'} Free Trial - No credit card required!</p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">4.2 Subscription Options</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-slate-200 rounded-lg my-4">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="border border-slate-200 px-4 py-2 text-left">Plan</th>
                        <th className="border border-slate-200 px-4 py-2 text-left">Price</th>
                        <th className="border border-slate-200 px-4 py-2 text-left">Features</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 font-semibold">Beta (Free Trial)</td>
                        <td className="border border-slate-200 px-4 py-2">Free for {process.env.REACT_APP_BETA_PERIOD || '1 month'}</td>
                        <td className="border border-slate-200 px-4 py-2">All core features, {process.env.REACT_APP_BETA_PAN_LIMIT || '1'} PAN limit, email support</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 font-semibold">Monthly Plan</td>
                        <td className="border border-slate-200 px-4 py-2">₹{process.env.REACT_APP_INDIVIDUAL_PRICE || '99'}/month</td>
                        <td className="border border-slate-200 px-4 py-2">All features + email support + {process.env.REACT_APP_INDIVIDUAL_PAN_LIMIT || '1'} PAN limit</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 font-semibold">Family</td>
                        <td className="border border-slate-200 px-4 py-2">₹{process.env.REACT_APP_FAMILY_PRICE || '199'}/month</td>
                        <td className="border border-slate-200 px-4 py-2">All features + priority support + {process.env.REACT_APP_FAMILY_PAN_LIMIT || '3'} PAN limit</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 font-semibold">Yearly Plan</td>
                        <td className="border border-slate-200 px-4 py-2">₹{process.env.REACT_APP_ANNUAL_PRICE || '699'}/year</td>
                        <td className="border border-slate-200 px-4 py-2">All features + priority support + {process.env.REACT_APP_ANNUAL_PAN_LIMIT || '3'} PAN limit + 365 days validity</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">4.3 Payment Terms</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li><strong className="text-slate-900">Currency:</strong> Indian Rupees (INR)</li>
                  <li><strong className="text-slate-900">Payment Processing:</strong> Secure Razorpay payment gateway</li>
                  <li><strong className="text-slate-900">Billing:</strong> Charged at subscription start, then recurring for monthly plans</li>
                  <li><strong className="text-slate-900">Taxes:</strong> All prices inclusive of applicable taxes</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">5. Data Processing & Privacy</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">5.1 Local Data Processing</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700">
                    <strong>Complete Privacy:</strong> Your CAMS statements and financial data are processed entirely on your PC using Power BI. We never receive, store, or have access to your financial information.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">5.2 Data Security</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li><strong className="text-slate-900">Account Security:</strong> Encrypted password storage and secure authentication</li>
                  <li><strong className="text-slate-900">Financial Data Security:</strong> Highest possible - data never leaves your PC</li>
                  <li><strong className="text-slate-900">License Protection:</strong> Secure license key system</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">6. Software License & Usage</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">6.1 License Grant</h3>
                <p className="text-slate-700 leading-relaxed">Subject to your compliance with these Terms and payment of applicable fees, we grant you a limited, non-exclusive, non-transferable license to:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Download and install the Portfolio Auditor software on your Windows PC</li>
                  <li>Use the software for personal portfolio analysis</li>
                  <li>Access updates and support during your subscription period</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">6.2 License Activation and Security</h3>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800">
                    <strong>Secure License Activation:</strong> When you activate your license on our application, we collect and encrypt your Hardware ID and PAN number. This secure mapping links your license key to your specific PC and prevents unauthorized use or abuse of our system. This information is stored securely and used solely for license validation and anti-piracy protection.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">6.3 Usage Restrictions</h3>
                <p className="text-slate-700 leading-relaxed">You may NOT:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Share your license key with others</li>
                  <li>Use the software for commercial portfolio analysis services</li>
                  <li>Reverse engineer or attempt to extract proprietary algorithms</li>
                  <li>Resell or redistribute the software</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">7. Financial Disclaimers</h2>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6">
              <h3 className="text-red-800 font-semibold mb-2">⚠️ Important Investment Disclaimer</h3>
              <p className="text-red-700 text-sm">
                Portfolio Auditor is an analysis tool only. We do not provide investment advice, recommendations, or financial planning services. All investment decisions are solely your responsibility.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-slate-900">7.1 No Investment Advice</h3>
            <p className="text-slate-700 leading-relaxed">Portfolio Auditor does NOT provide:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Investment recommendations or advice</li>
                <li>Market timing or trading suggestions</li>
                <li>Financial planning or wealth management services</li>
                <li>Tax advice or guidance</li>
              </ul>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">7.2 Analysis Limitations</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Based on historical performance data only</li>
                  <li>Past performance does not guarantee future results</li>
                  <li>Analysis assumes perfect execution without considering transaction costs or taxes</li>
                  <li>Market conditions and fund management changes may affect accuracy</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">8. User Responsibilities</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">8.1 Account Requirements</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Provide accurate registration information</li>
                  <li>Maintain account security and password confidentiality</li>
                  <li>Use valid CAMS statements for analysis</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">8.2 System Requirements</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Maintain compatible Windows PC system</li>
                  <li>Install required Microsoft Power BI Desktop</li>
                  <li>Ensure adequate system resources for analysis</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">9. Refund Policy</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">9.1 {process.env.REACT_APP_BETA_DURATION?.split('-')[0] || '7'}-Day Money-Back Guarantee</h3>
                <p className="text-slate-700 leading-relaxed">We offer a {process.env.REACT_APP_BETA_DURATION?.split(' ')[0]?.toLowerCase() || '7'}-day money-back guarantee for new subscriptions if:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Software fails to install or function on your compatible system</li>
                  <li>Technical issues prevent proper analysis functionality</li>
                  <li>Service not delivered as described</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">9.2 Non-Refundable Situations</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>User dissatisfaction with analysis results</li>
                  <li>Change of mind after successful software delivery</li>
                  <li>User error in system requirements or usage</li>
                  <li>Requests made after {process.env.REACT_APP_BETA_DURATION?.split(' ')[0]?.toLowerCase() || '7'}-day guarantee period</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">10. Shipping & Delivery Policy</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">10.1 Digital Delivery</h3>
                <p className="text-slate-700 leading-relaxed">
                  For International or Indian buyers, as soon as orders are placed on our website, Software bundle along with License key will be available to download and customer can install and activate the license and continue with analysis on their own PC.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">10.2 Service Responsibility</h3>
                <p className="text-slate-700 leading-relaxed">
                  Portfolio Auditor team is not liable for any issues in the order processing beyond our control. For any issues in utilizing our Software/Tool you may contact our helpdesk:
                </p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li><strong className="text-slate-900">Phone:</strong> +91 9629685970</li>
                  <li><strong className="text-slate-900">Email:</strong> {process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-blue-800 font-semibold mb-2">📦 Instant Digital Delivery</h4>
                <p className="text-blue-700 text-sm mb-0">
                  <strong>No Physical Shipping:</strong> This is a digital software product. There are no physical items to ship. All software and license keys are delivered digitally through your account dashboard immediately after successful payment.
                </p>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">11. Service Availability</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">11.1 Software Updates</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Regular software updates and feature enhancements</li>
                  <li>Bug fixes and performance improvements</li>
                  <li>New analysis features and capabilities</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">11.2 Future Roadmap</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Stock portfolio analysis (coming soon)</li>
                  <li>NPS and pension fund analysis (planned)</li>
                  <li>Mac compatibility (in development)</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">12. Limitation of Liability</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">12.1 Service Disclaimer</h3>
                <p className="text-slate-700 leading-relaxed">THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, INCLUDING:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Accuracy of analysis or investment performance predictions</li>
                  <li>Uninterrupted or error-free operation</li>
                  <li>Fitness for particular investment purposes</li>
                  <li>Compatibility with all system configurations</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">12.2 Liability Limitations</h3>
                <p className="text-slate-700 leading-relaxed">To the maximum extent permitted by law:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Our liability is limited to the amount paid for your subscription</li>
                  <li>We are not liable for any investment losses or financial damages</li>
                  <li>No liability for indirect, incidental, or consequential damages</li>
                  <li>Users assume full responsibility for investment decisions</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">13. Termination</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">13.1 User-Initiated Termination</h3>
                <p className="text-slate-700 leading-relaxed">You may cancel your subscription at any time:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Monthly Plan: Cancel anytime, access until end of billing period</li>
                  <li>Family plans: Cancel anytime, access until end of billing period</li>
                  <li>Yearly Plan: Cancel anytime, access until end of subscription period</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">13.2 Company-Initiated Termination</h3>
                <p className="text-slate-700 leading-relaxed">We may terminate accounts for:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Violation of these Terms</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>License key sharing or misuse</li>
                  <li>Non-payment of subscription fees</li>
                </ul>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">14. Beta Program Terms</h2>
              
              <h3 className="text-xl font-semibold text-slate-900">14.1 Beta Features</h3>
              <p className="text-slate-700 leading-relaxed">During our Beta phase:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Some features may be in testing or development</li>
                <li>We may collect anonymous usage feedback for improvements</li>
                <li>Beta users get early access to new features</li>
                <li>Standard terms and privacy protections apply</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">15. Modifications to Terms</h2>
              <p className="text-slate-700 leading-relaxed">We may update these Terms with reasonable notice:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Posted on our website with "Last Updated" date</li>
                <li>Email notifications for significant changes</li>
                <li>Continued use constitutes acceptance of updated Terms</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">16. Contact Information</h2>
              <p className="text-slate-700 leading-relaxed">For questions about these Terms and Conditions:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong className="text-slate-900">Email:</strong> {process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}</li>
                <li><strong className="text-slate-900">Website:</strong> {process.env.REACT_APP_COMPANY_WEBSITE || 'www.portfolioauditor.com'}</li>
              </ul>
            </section>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mt-8">
              <h3 className="text-slate-900 font-semibold mb-2">Terms Summary</h3>
              <p className="text-slate-700 text-sm mb-0">
                Portfolio Auditor provides privacy-first portfolio analysis software with flexible subscription options. Your financial data remains completely private on your PC, while we provide the tools and support you need for comprehensive investment analysis.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;